import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Download, Search, ChevronDown, ChevronLeft, ChevronRight, Filter, Handshake, CheckCircle, Clock } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import EventNavigation from '../components/EventNavigation';
import LoadingSpinner from '../components/LoadingSpinner';
import MeetingDetailsDrawer from '../components/MeetingDetailsDrawer';
import MeetingRequestDrawer from '../components/MeetingRequestDrawer';
import useEventMeetings from '../hooks/useEventMeetings';
import api from '../services/api';
import '../components/EventHome.css';
import './EventEntityCompanies.css';

/**
 * EventPhilanthropists - Displays philanthropists for an event (EventEntity ID 2)
 * Card-based view showing organization details
 */
const EventPhilanthropists = () => {
    const { eventNameId } = useParams();
    const eventEntityId = 2; // Fixed: Philanthropists are EventEntity 2
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tableData, setTableData] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [filterView, setFilterView] = useState('main'); // 'main', 'areas', 'geo'
    const [selectedAreasOfGiving, setSelectedAreasOfGiving] = useState([]);
    const [selectedGeographicFocus, setSelectedGeographicFocus] = useState([]);
    const [selectedGivingRegions, setSelectedGivingRegions] = useState([]); // Question 53 - Giving Regions
    const [filterSearchAreas, setFilterSearchAreas] = useState('');
    const [filterSearchGeo, setFilterSearchGeo] = useState('');
    const [filterSearchGivingRegions, setFilterSearchGivingRegions] = useState('');
    
    // Meeting details drawer state
    const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
    const [selectedMeetingId, setSelectedMeetingId] = useState(null);
    const [selectedMeetingCompanyName, setSelectedMeetingCompanyName] = useState(null);
    
    // Meeting request drawer state
    const [meetingRequestDrawerOpen, setMeetingRequestDrawerOpen] = useState(false);
    const [meetingRequestContact, setMeetingRequestContact] = useState(null);
    
    const filterWrapperRef = useRef(null);
    
    // Use event meetings hook - fetches meetings from iConnections SQL Server
    const {
        getMeetingStatusForCompany,
        refresh: refreshMeetings
    } = useEventMeetings(event?.EventId, event?.ic_event_id, { autoFetch: false });

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    // Fetch meetings when event is loaded
    useEffect(() => {
        if (event?.EventId && event?.ic_event_id) {
            refreshMeetings();
        }
    }, [event?.EventId, event?.ic_event_id, refreshMeetings]);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
                setFilterDropdownOpen(false);
                setFilterView('main');
            }
        };

        if (filterDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [filterDropdownOpen]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch event data
            const eventResponse = await api.get(`/public/events/${eventNameId}`);
            setEvent(eventResponse.data);

            // Fetch companies with profile data for philanthropists (entity ID 2)
            const tableResponse = await api.get(`/event-entity-companies/${eventEntityId}/profile`);
            setTableData(tableResponse.data);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.response?.data?.detail || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to find question by keywords in question_text or short_description
    const findQuestionByKeywords = (keywords) => {
        if (!tableData?.questions) return null;
        return tableData.questions.find(q => {
            const text = (q.question_text + ' ' + (q.short_description || '')).toLowerCase();
            return keywords.some(kw => text.includes(kw.toLowerCase()));
        });
    };

    // Get specific field values for a company
    const getFieldValue = (company, keywords) => {
        const question = findQuestionByKeywords(keywords);
        if (!question) return null;
        return company.answers[question.question_id];
    };

    // Get field value by specific question ID
    const getFieldValueById = (company, questionId) => {
        return company.answers[questionId] || null;
    };

    // Clean up geographic names by removing extra suffixes
    const cleanGeographicName = (name) => {
        if (!name) return name;
        return name
            .replace(/, United Republic of$/i, '')
            .replace(/, State of$/i, '')
            .replace(/, Republic of$/i, '')
            .replace(/, Kingdom of$/i, '')
            .replace(/, Federation of$/i, '')
            .replace(/, Democratic Republic of$/i, '')
            .replace(/, People's Republic of$/i, '')
            .trim();
    };

    // First, collect all known options from hierarchical data across all companies
    const knownOptions = useMemo(() => {
        if (!tableData?.companies) return { areas: new Set(), geo: new Set() };

        const areas = new Set();
        const geo = new Set();

        tableData.companies.forEach(company => {
            // Extract from areas of philanthropic giving (question ID 43) - include subcategories with parent prefix
            const areasValue = getFieldValueById(company, 43);
            if (areasValue && typeof areasValue === 'object' && areasValue.type === 'hierarchical' && areasValue.items) {
                areasValue.items.forEach(item => {
                    areas.add(item.category);
                    if (item.subcategories) {
                        item.subcategories.forEach(sub => areas.add(`${item.category}: ${sub}`));
                    }
                });
            }

            // Extract from geographic focus
            const geoValue = getFieldValue(company, ['geographic', 'region', 'states', 'footprint']);
            if (geoValue && typeof geoValue === 'object' && geoValue.type === 'hierarchical' && geoValue.items) {
                geoValue.items.forEach(item => geo.add(item.category));
            } else if (geoValue && typeof geoValue === 'string') {
                // Handle comma-separated values (backend now converts country codes to names)
                const values = geoValue.split(',').map(s => cleanGeographicName(s.trim())).filter(Boolean);
                values.forEach(g => geo.add(g));
            }
        });

        return { areas, geo };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData?.companies]);

    // Extract items from hierarchical value or return as array
    const extractTags = (value, optionSet = null, isGeographic = false, includeSubcategories = false) => {
        if (!value) return [];
        if (typeof value === 'object' && value.type === 'hierarchical' && value.items) {
            if (includeSubcategories) {
                return value.items.map(item => ({
                    category: isGeographic ? cleanGeographicName(item.category) : item.category,
                    subcategories: item.subcategories || []
                }));
            }
            return value.items.map(item => isGeographic ? cleanGeographicName(item.category) : item.category);
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();

            // For geographic data (states/countries), clean up first then split
            if (isGeographic) {
                const cleaned = trimmed
                    .replace(/, United Republic of/gi, '')
                    .replace(/, State of/gi, '')
                    .replace(/, Republic of/gi, '')
                    .replace(/, Kingdom of/gi, '')
                    .replace(/, Federation of/gi, '')
                    .replace(/, Democratic Republic of the/gi, '')
                    .replace(/, Democratic Republic of/gi, '')
                    .replace(/, People's Republic of/gi, '');

                return cleaned.split(',').map(s => s.trim()).filter(Boolean);
            }

            // If we have known options, try to match against them
            if (optionSet && optionSet.size > 0) {
                const matched = [];
                let remaining = trimmed;

                const sortedOptions = Array.from(optionSet).sort((a, b) => b.length - a.length);

                for (const option of sortedOptions) {
                    if (remaining.includes(option)) {
                        matched.push(option);
                        remaining = remaining.replace(option, '|||MATCHED|||');
                    }
                }

                if (matched.length > 0) {
                    if (includeSubcategories) {
                        return matched.map(m => ({ category: m, subcategories: [] }));
                    }
                    return matched;
                }
            }

            if (includeSubcategories) {
                return [{ category: trimmed, subcategories: [] }];
            }
            return [trimmed];
        }
        if (Array.isArray(value)) {
            if (includeSubcategories) {
                return value.map(v => ({ category: v, subcategories: [] }));
            }
            return value;
        }
        return [];
    };

    // Extract flat list of all tags (categories + subcategories) for filtering
    const extractAllFilterTags = (value, optionSet = null) => {
        const structured = extractTags(value, optionSet, false, true);
        const allTags = [];
        structured.forEach(item => {
            if (typeof item === 'object' && item.category) {
                allTags.push(item.category);
                if (item.subcategories) {
                    item.subcategories.forEach(sub => allTags.push(`${item.category}: ${sub}`));
                }
            } else {
                allTags.push(item);
            }
        });
        return allTags;
    };

    // Extract distinct values for filters
    const { distinctAreasOfGiving, distinctGeographicFocus, distinctGivingRegions } = useMemo(() => {
        if (!tableData?.companies) return { distinctAreasOfGiving: [], distinctGeographicFocus: [], distinctGivingRegions: [] };

        const areasSet = new Set();
        const geoSet = new Set();
        const givingRegionsSet = new Set(); // Question 53 - Giving Regions

        tableData.companies.forEach(company => {
            const areas = extractAllFilterTags(getFieldValueById(company, 43), knownOptions.areas);
            // Question 85 - Geographic Focus (comma-separated values)
            const geoFocusValue = getFieldValueById(company, 85);
            const geo = geoFocusValue ? extractTags(geoFocusValue, null, true, false) : [];
            
            // Question 53 - Giving Regions (comma-separated values)
            const givingRegionsValue = getFieldValueById(company, 53);
            if (givingRegionsValue && givingRegionsValue !== '{}') {
                const givingRegionsTags = extractTags(givingRegionsValue, null, true, false)
                    .filter(g => g && g !== '{}' && g.trim() !== '');
                givingRegionsTags.forEach(g => givingRegionsSet.add(g));
            }

            areas.forEach(a => areasSet.add(a));
            geo.forEach(g => geoSet.add(g));
        });

        // Sort Giving Regions with "United States" at top, then alphabetical
        const sortedGivingRegions = Array.from(givingRegionsSet).sort((a, b) => {
            if (a === 'United States') return -1;
            if (b === 'United States') return 1;
            return a.localeCompare(b);
        });
        
        return {
            distinctAreasOfGiving: Array.from(areasSet).sort(),
            distinctGeographicFocus: Array.from(geoSet).sort(),
            distinctGivingRegions: sortedGivingRegions
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData?.companies, knownOptions]);

    // Filter companies based on search query and selected filters, then sort by company name
    const filteredCompanies = useMemo(() => {
        if (!tableData?.companies) return [];

        return tableData.companies.filter(company => {
            // Search filter
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const matchesSearch =
                    company.CompanyName?.toLowerCase().includes(query) ||
                    Object.values(company.answers || {}).some(answer => {
                        if (typeof answer === 'string' && answer.toLowerCase().includes(query)) return true;
                        if (typeof answer === 'object' && answer?.type === 'hierarchical' && answer.items) {
                            return answer.items.some(item => item.category?.toLowerCase().includes(query));
                        }
                        return false;
                    }) ||
                    // Search through organization contacts
                    (company.contacts || []).some(contact => {
                        const fullName = `${contact.FirstName || ''} ${contact.LastName || ''}`.toLowerCase();
                        const title = (contact.Title || '').toLowerCase();
                        return fullName.includes(query) || title.includes(query);
                    });
                if (!matchesSearch) return false;
            }

            // Areas of Philanthropic Giving filter (includes subcategories)
            if (selectedAreasOfGiving.length > 0) {
                const companyAreas = extractAllFilterTags(getFieldValueById(company, 43), knownOptions.areas);
                const hasMatchingArea = selectedAreasOfGiving.some(filter => companyAreas.includes(filter));
                if (!hasMatchingArea) return false;
            }

            // Geographic Focus filter (Question 85)
            if (selectedGeographicFocus.length > 0) {
                const geoFocusValue = getFieldValueById(company, 85);
                const companyGeo = geoFocusValue ? extractTags(geoFocusValue, null, true, false) : [];
                const hasMatchingGeo = selectedGeographicFocus.some(filter => companyGeo.includes(filter));
                if (!hasMatchingGeo) return false;
            }

            // Giving Regions filter (Question 53)
            if (selectedGivingRegions.length > 0) {
                const givingRegionsValue = getFieldValueById(company, 53);
                const companyGivingRegions = (givingRegionsValue && givingRegionsValue !== '{}') 
                    ? extractTags(givingRegionsValue, null, true, false).filter(g => g && g !== '{}' && g.trim() !== '')
                    : [];
                const hasMatchingGivingRegion = selectedGivingRegions.some(filter => companyGivingRegions.includes(filter));
                if (!hasMatchingGivingRegion) return false;
            }

            return true;
        }).sort((a, b) => {
            // Default sort by company name (alphabetical, trimmed for leading/trailing spaces)
            const nameA = (a.CompanyName || '').trim().toLowerCase();
            const nameB = (b.CompanyName || '').trim().toLowerCase();
            return nameA.localeCompare(nameB);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData?.companies, searchQuery, selectedAreasOfGiving, selectedGeographicFocus, selectedGivingRegions, knownOptions]);

    // Toggle filter selection
    const toggleFilter = (filterType, value) => {
        if (filterType === 'areas') {
            setSelectedAreasOfGiving(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        } else if (filterType === 'geo') {
            setSelectedGeographicFocus(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        } else if (filterType === 'givingRegions') {
            setSelectedGivingRegions(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        }
    };

    // Clear all filters
    const clearFilters = () => {
        setSelectedAreasOfGiving([]);
        setSelectedGeographicFocus([]);
        setSelectedGivingRegions([]);
    };

    const activeFilterCount = selectedAreasOfGiving.length + selectedGeographicFocus.length + selectedGivingRegions.length;

    const handleCardClick = (company) => {
        setSelectedCompany(company);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setTimeout(() => setSelectedCompany(null), 300);
    };

    const renderDrawerValue = (value, question) => {
        if (!value) {
            return <span className="drawer-empty-value">Not provided</span>;
        }

        if (typeof value === 'object' && value !== null && value.type === 'file' && value.url) {
            const isImage = value.url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const s3Key = value.s3_key || value.url;
            
            const handleFileClick = async (e) => {
                e.preventDefault();
                try {
                    const companyName = selectedCompany?.CompanyName || '';
                    const questionLabel = question.short_description || question.question_text || '';
                    const response = await api.post('/event-entity-companies/file-download', null, {
                        params: {
                            s3_key: s3Key,
                            company_name: companyName,
                            question_label: questionLabel
                        }
                    });
                    if (response.data?.presigned_url) {
                        window.open(response.data.presigned_url, '_blank');
                    }
                } catch (err) {
                    console.error('Error fetching file URL:', err);
                    // Fallback to direct URL if endpoint fails
                    window.open(value.url, '_blank');
                }
            };
            
            return (
                <div className="drawer-file-section">
                    {isImage ? (
                        <div className="drawer-image-preview">
                            <img src={value.url} alt={question.question_text} />
                        </div>
                    ) : null}
                    <button
                        onClick={handleFileClick}
                        className="drawer-file-link"
                    >
                        <Download size={16} />
                        View File
                    </button>
                </div>
            );
        }

        // Check if this field should render as chips
        const fieldName = (question.short_description || question.question_text || '').toLowerCase();
        const shouldRenderAsChips = 
            fieldName.includes('areas of philanthropic giving') ||
            fieldName.includes('geographic focus') ||
            fieldName.includes('potential geographies for future giving') ||
            fieldName.includes('affinity group') ||
            question.question_id === 53 || // Giving Regions
            question.question_id === 85; // Geographic Focus

        if (typeof value === 'object' && value !== null && value.type === 'hierarchical' && Array.isArray(value.items)) {
            // Render as chips for specific fields
            if (shouldRenderAsChips) {
                return (
                    <div className="nonprofit-card-tags">
                        {value.items.map((item, idx) => (
                            <span key={idx} className="nonprofit-tag">
                                {item.category}
                                {item.subcategories && item.subcategories.length > 0 && (
                                    <span className="nonprofit-tag-subs">
                                        {item.subcategories.map((sub, subIdx) => (
                                            <span key={subIdx}> [{sub}]{subIdx < item.subcategories.length - 1 ? ',' : ''}</span>
                                        ))}
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                );
            }
            
            return (
                <div className="drawer-hierarchical-list">
                    {value.items.map((item, idx) => (
                        <div key={idx} className="drawer-category-group">
                            <div className="drawer-category-main">{item.category}</div>
                            {item.subcategories && item.subcategories.length > 0 && (
                                <>
                                    {item.subcategories.map((sub, subIdx) => (
                                        <div key={subIdx} className="drawer-category-sub">
                                            {sub}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        // Render string/array values as chips for specific fields
        if (shouldRenderAsChips && (typeof value === 'string' || Array.isArray(value))) {
            let tags = [];
            if (typeof value === 'string') {
                tags = value.split(',').map(t => t.trim()).filter(t => t);
            } else if (Array.isArray(value)) {
                tags = value.map(t => typeof t === 'string' ? t.trim() : String(t)).filter(t => t);
            }
            if (tags.length > 0) {
                return (
                    <div className="nonprofit-card-tags">
                        {tags.map((tag, idx) => (
                            <span key={idx} className="nonprofit-tag">{tag}</span>
                        ))}
                    </div>
                );
            }
        }

        const textValue = String(value);

        // Check if it's a URL and make it clickable
        const isUrl = textValue.match(/^(https?:\/\/|www\.)/i) ||
            (textValue.includes('.') && !textValue.includes(' ') &&
                (question.question_text?.toLowerCase().includes('website') ||
                    question.short_description?.toLowerCase().includes('website')));

        if (isUrl) {
            const href = textValue.startsWith('http') ? textValue : `https://${textValue}`;
            return (
                <div className="drawer-text-value">
                    <a href={href} target="_blank" rel="noopener noreferrer" className="drawer-link">
                        {textValue}
                    </a>
                </div>
            );
        }

        return (
            <div className="drawer-text-value">
                {textValue.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                        {line}
                        {i < textValue.split('\n').length - 1 && <br />}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading philanthropists..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate(`/event/${eventNameId}/home`)} className="btn btn-primary">
                                Return to Event Home
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Get mission statement - use question ID 40 directly, filter out non-meaningful answers
    const getMissionStatement = (company) => {
        // First try the specific mission statement question (ID 40)
        const missionAnswer = company.answers[40];
        if (missionAnswer && typeof missionAnswer === 'string') {
            const normalized = missionAnswer.toLowerCase().trim();
            // Skip non-meaningful answers
            const skipValues = ['yes', 'no', 'none', 'n/a', 'na', 'not applicable', 'not available', '-', ''];
            if (!skipValues.includes(normalized) && normalized.length > 0) {
                return missionAnswer;
            }
        }
        return null;
    };

    // Handle opening meeting details drawer
    const handleMeetingClick = (e, meeting, companyName) => {
        e.stopPropagation(); // Prevent card click
        setSelectedMeetingId(meeting.meetingId);
        setSelectedMeetingCompanyName(companyName);
        setMeetingDrawerOpen(true);
    };

    // Handle meeting drawer close
    const handleMeetingDrawerClose = () => {
        setMeetingDrawerOpen(false);
        setSelectedMeetingId(null);
        setSelectedMeetingCompanyName(null);
    };

    // Handle meeting action completed (refresh meetings list)
    const handleMeetingAction = () => {
        refreshMeetings();
    };

    // Handle opening meeting request drawer
    const handleMeetRequest = (company) => {
        // Get the first contact with ic_contact_id for the meeting request
        const contactWithIcId = company.contacts?.find(c => c.ic_contact_id);
        
        if (contactWithIcId) {
            setMeetingRequestContact({
                contact_id: contactWithIcId.ContactId,
                first_name: contactWithIcId.FirstName,
                last_name: contactWithIcId.LastName,
                job_title: contactWithIcId.JobTitle,
                company_name: company.CompanyName,
                image_url: contactWithIcId.ImageUrl,
                ic_contact_id: contactWithIcId.ic_contact_id
            });
            setMeetingRequestDrawerOpen(true);
        } else {
            // No contact with ic_contact_id - show an alert or handle gracefully
            console.warn('No contact with iConnections ID found for meeting request');
        }
    };

    // Handle meeting request drawer close
    const handleMeetingRequestClose = () => {
        setMeetingRequestDrawerOpen(false);
        setMeetingRequestContact(null);
    };

    // Handle meeting request success
    const handleMeetingRequestSuccess = () => {
        setMeetingRequestDrawerOpen(false);
        setMeetingRequestContact(null);
        refreshMeetings();
    };

    // Render meeting status badge/button for a company card
    const renderMeetingStatus = (company) => {
        // Use ic_event_company_id (iConnections ID) for matching against meetings
        const icEventCompanyId = company.ic_event_company_id ? parseInt(company.ic_event_company_id, 10) : null;
        const meetingInfo = getMeetingStatusForCompany(icEventCompanyId);
        
        if (!meetingInfo) {
            return null; // No meeting with this company
        }

        const { status, meeting } = meetingInfo;

        if (status === 'confirmed') {
            return (
                <button 
                    className="card-meeting-badge confirmed"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <CheckCircle size={14} />
                    <span>Meeting Confirmed</span>
                </button>
            );
        }

        if (status === 'pending_sent') {
            return (
                <button 
                    className="card-meeting-badge pending-sent"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <Clock size={14} />
                    <span>Meeting Pending</span>
                </button>
            );
        }

        if (status === 'pending_received') {
            return (
                <button 
                    className="card-meeting-badge pending-received"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <Clock size={14} />
                    <span>Meeting Action Required</span>
                </button>
            );
        }

        if (status === 'cancelled') {
            return (
                <button 
                    className="card-meeting-badge cancelled"
                    onClick={(e) => handleMeetingClick(e, meeting, company.CompanyName)}
                >
                    <X size={14} />
                    <span>Meeting Cancelled</span>
                </button>
            );
        }

        return null;
    };

    // Render a philanthropist card
    const renderPhilanthropistCard = (company) => {
        const philanthropyType = getFieldValue(company, ['philanthropy type', 'type of philanthropy', 'donor type']);
        const missionStatement = getMissionStatement(company);
        const areasOfGivingStructured = extractTags(getFieldValueById(company, 43), knownOptions.areas, false, true);
        
        // Question 85 - Geographic Focus (comma-separated values)
        const geographicFocusValue = getFieldValueById(company, 85);
        const geographicFocus = geographicFocusValue ? extractTags(geographicFocusValue, null, true, false) : [];
        
        // Question 53 - Giving Regions (comma-separated values)
        const givingRegionsValue = getFieldValueById(company, 53);
        const givingRegions = (givingRegionsValue && givingRegionsValue !== '{}') 
            ? extractTags(givingRegionsValue, null, true, false).filter(g => g && g !== '{}' && g.trim() !== '')
            : [];

        return (
            <div
                key={company.EventCompanyId}
                className="nonprofit-card"
                onClick={() => handleCardClick(company)}
            >
                <div className="nonprofit-card-header no-icon">
                    <div className="nonprofit-card-title-section">
                        <h3 className="nonprofit-card-name">{company.CompanyName}</h3>
                        {philanthropyType && (
                            <div className="nonprofit-card-location">
                                <span>{philanthropyType}</span>
                            </div>
                        )}
                    </div>
                </div>

                {missionStatement && (
                    <p className="nonprofit-card-description">{missionStatement}</p>
                )}

                {areasOfGivingStructured.length > 0 && (
                    <div className="nonprofit-card-section">
                        <label className="nonprofit-card-label">AREAS OF PHILANTHROPIC GIVING</label>
                        <div className="nonprofit-card-tags">
                            {areasOfGivingStructured.map((item, idx) => (
                                <span key={idx} className="nonprofit-tag">
                                    {item.category}
                                    {item.subcategories && item.subcategories.length > 0 && (
                                        <span className="nonprofit-tag-subs">
                                            {item.subcategories.map((sub, subIdx) => (
                                                <span key={subIdx}> [{sub}]{subIdx < item.subcategories.length - 1 ? ',' : ''}</span>
                                            ))}
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {geographicFocus.length > 0 && (
                    <div className="nonprofit-card-section">
                        <label className="nonprofit-card-label">GEOGRAPHIC FOCUS</label>
                        <div className="nonprofit-card-tags">
                            {geographicFocus.map((tag, idx) => (
                                <span key={idx} className="nonprofit-tag">{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                {givingRegions.length > 0 && (
                    <div className="nonprofit-card-section">
                        <label className="nonprofit-card-label">GIVING REGIONS</label>
                        <div className="nonprofit-card-tags">
                            {givingRegions.map((tag, idx) => (
                                <span key={idx} className="nonprofit-tag">{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                {company.contacts && company.contacts.length > 0 && (
                    <div className="nonprofit-card-contacts">
                        <label className="nonprofit-card-label">ORGANIZATION CONTACTS</label>
                        <div className="nonprofit-contacts-grid">
                            {company.contacts.map((contact) => (
                                <div key={contact.ContactId} className="nonprofit-contact-item">
                                    {contact.ImageUrl ? (
                                        <img
                                            src={contact.ImageUrl}
                                            alt={`${contact.FirstName} ${contact.LastName}`}
                                            className="nonprofit-contact-avatar"
                                        />
                                    ) : (
                                        <div className="nonprofit-contact-avatar-placeholder">
                                            {contact.FirstName?.[0]}{contact.LastName?.[0]}
                                        </div>
                                    )}
                                    <div className="nonprofit-contact-info">
                                        <div className="nonprofit-contact-name">
                                            {contact.FirstName} {contact.LastName}
                                        </div>
                                        {contact.JobTitle && (
                                            <div className="nonprofit-contact-title">
                                                {contact.JobTitle}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Meeting Status Section */}
                {renderMeetingStatus(company) && (
                    <div className="nonprofit-card-meeting-section">
                        {renderMeetingStatus(company)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar event-entity-companies-page nonprofit-cards-page">
                    <div className="event-content">
                        <div className="page-header nonprofit-page-header">
                            <div className="nonprofit-header-content">
                                <h1>Philanthropists ({filteredCompanies.length})</h1>
                                <p className="nonprofit-subtitle">
                                    Connect with philanthropists and donors participating in {event?.EventName || 'Funds for Charity 2026'}
                                </p>
                            </div>
                        </div>

                        <div className="nonprofit-search-bar">
                            <div className="nonprofit-search-input-wrapper">
                                <Search size={18} className="nonprofit-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by organization name, mission, focus area, or contact..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="nonprofit-search-input"
                                />
                            </div>
                            <div className="nonprofit-filter-wrapper" ref={filterWrapperRef}>
                                <button
                                    className={`nonprofit-filter-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
                                    onClick={() => {
                                        setFilterDropdownOpen(!filterDropdownOpen);
                                        if (!filterDropdownOpen) setFilterView('main');
                                    }}
                                >
                                    <Filter size={16} />
                                    {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Add filter'}
                                    <ChevronDown size={16} className={filterDropdownOpen ? 'rotated' : ''} />
                                </button>

                                {filterDropdownOpen && (
                                    <div className="nonprofit-filter-dropdown">
                                        {/* Main menu - select filter type */}
                                        {filterView === 'main' && (
                                            <>
                                                <div className="filter-menu-item" onClick={() => setFilterView('areas')}>
                                                    <span>Areas of Philanthropic Giving</span>
                                                    <div className="filter-menu-item-right">
                                                        {selectedAreasOfGiving.length > 0 && (
                                                            <span className="filter-count-badge">{selectedAreasOfGiving.length}</span>
                                                        )}
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </div>
                                                <div className="filter-menu-item" onClick={() => setFilterView('geo')}>
                                                    <span>Geographic Focus</span>
                                                    <div className="filter-menu-item-right">
                                                        {selectedGeographicFocus.length > 0 && (
                                                            <span className="filter-count-badge">{selectedGeographicFocus.length}</span>
                                                        )}
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </div>
                                                <div className="filter-menu-item" onClick={() => setFilterView('givingRegions')}>
                                                    <span>Giving Regions</span>
                                                    <div className="filter-menu-item-right">
                                                        {selectedGivingRegions.length > 0 && (
                                                            <span className="filter-count-badge">{selectedGivingRegions.length}</span>
                                                        )}
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </div>
                                                {activeFilterCount > 0 && (
                                                    <div className="filter-actions">
                                                        <button className="filter-clear-btn" onClick={clearFilters}>
                                                            Clear all filters
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Areas of Philanthropic Giving options */}
                                        {filterView === 'areas' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => { setFilterView('main'); setFilterSearchAreas(''); }}>
                                                    <ChevronLeft size={16} />
                                                    <span>Areas of Philanthropic Giving</span>
                                                </div>
                                                <div className="filter-search-wrapper">
                                                    <Search size={14} className="filter-search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search areas..."
                                                        value={filterSearchAreas}
                                                        onChange={(e) => setFilterSearchAreas(e.target.value)}
                                                        className="filter-search-input"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="filter-options">
                                                    {distinctAreasOfGiving
                                                        .filter(area => area.toLowerCase().includes(filterSearchAreas.toLowerCase()))
                                                        .map(area => (
                                                        <label key={area} className="filter-option">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedAreasOfGiving.includes(area)}
                                                                onChange={() => toggleFilter('areas', area)}
                                                            />
                                                            <span>{area}</span>
                                                        </label>
                                                    ))}
                                                    {distinctAreasOfGiving.filter(area => area.toLowerCase().includes(filterSearchAreas.toLowerCase())).length === 0 && (
                                                        <span className="filter-empty">
                                                            {filterSearchAreas ? 'No matching options' : 'No options available'}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* Geographic Focus options (Question 85) */}
                                        {filterView === 'geo' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => { setFilterView('main'); setFilterSearchGeo(''); }}>
                                                    <ChevronLeft size={16} />
                                                    <span>Geographic Focus</span>
                                                </div>
                                                <div className="filter-search-wrapper">
                                                    <Search size={14} className="filter-search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search regions..."
                                                        value={filterSearchGeo}
                                                        onChange={(e) => setFilterSearchGeo(e.target.value)}
                                                        className="filter-search-input"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="filter-options">
                                                    {distinctGeographicFocus
                                                        .filter(geo => geo.toLowerCase().includes(filterSearchGeo.toLowerCase()))
                                                        .map(geo => (
                                                        <label key={geo} className="filter-option">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedGeographicFocus.includes(geo)}
                                                                onChange={() => toggleFilter('geo', geo)}
                                                            />
                                                            <span>{geo}</span>
                                                        </label>
                                                    ))}
                                                    {distinctGeographicFocus.filter(geo => geo.toLowerCase().includes(filterSearchGeo.toLowerCase())).length === 0 && (
                                                        <span className="filter-empty">
                                                            {filterSearchGeo ? 'No matching options' : 'No options available'}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* Giving Regions options (Question 53) */}
                                        {filterView === 'givingRegions' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => { setFilterView('main'); setFilterSearchGivingRegions(''); }}>
                                                    <ChevronLeft size={16} />
                                                    <span>Giving Regions</span>
                                                </div>
                                                <div className="filter-search-wrapper">
                                                    <Search size={14} className="filter-search-icon" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search regions..."
                                                        value={filterSearchGivingRegions}
                                                        onChange={(e) => setFilterSearchGivingRegions(e.target.value)}
                                                        className="filter-search-input"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                {distinctGivingRegions.length > 0 && (
                                                    <div className="filter-select-all">
                                                        <button
                                                            className="filter-select-all-btn"
                                                            onClick={() => {
                                                                if (selectedGivingRegions.length === distinctGivingRegions.length) {
                                                                    setSelectedGivingRegions([]);
                                                                } else {
                                                                    setSelectedGivingRegions([...distinctGivingRegions]);
                                                                }
                                                            }}
                                                        >
                                                            {selectedGivingRegions.length === distinctGivingRegions.length ? 'Clear All' : 'Select All'}
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="filter-options">
                                                    {distinctGivingRegions
                                                        .filter(region => region.toLowerCase().includes(filterSearchGivingRegions.toLowerCase()))
                                                        .map(region => (
                                                        <label key={region} className="filter-option">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedGivingRegions.includes(region)}
                                                                onChange={() => toggleFilter('givingRegions', region)}
                                                            />
                                                            <span>{region}</span>
                                                        </label>
                                                    ))}
                                                    {distinctGivingRegions.filter(region => region.toLowerCase().includes(filterSearchGivingRegions.toLowerCase())).length === 0 && (
                                                        <span className="filter-empty">
                                                            {filterSearchGivingRegions ? 'No matching options' : 'No options available'}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Active filter tags */}
                        {activeFilterCount > 0 && (
                            <div className="nonprofit-active-filters">
                                {selectedAreasOfGiving.map(area => (
                                    <span key={`area-${area}`} className="active-filter-tag">
                                        {area}
                                        <button onClick={() => toggleFilter('areas', area)}>
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                                {selectedGeographicFocus.map(geo => (
                                    <span key={`geo-${geo}`} className="active-filter-tag">
                                        {geo}
                                        <button onClick={() => toggleFilter('geo', geo)}>
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                                {selectedGivingRegions.map(region => (
                                    <span key={`region-${region}`} className="active-filter-tag">
                                        {region}
                                        <button onClick={() => toggleFilter('givingRegions', region)}>
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {!tableData || tableData.companies.length === 0 ? (
                            <div className="empty-state">
                                <Handshake size={48} />
                                <h3>No Philanthropists Registered</h3>
                                <p>There are no philanthropists registered for this event yet.</p>
                            </div>
                        ) : filteredCompanies.length === 0 ? (
                            <div className="empty-state">
                                <Search size={48} />
                                <h3>No Results Found</h3>
                                <p>No philanthropists match your search criteria. Try adjusting your search.</p>
                            </div>
                        ) : (
                            <div className="nonprofit-cards-container">
                                {filteredCompanies.map(company => renderPhilanthropistCard(company))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Profile Drawer */}
            {drawerOpen && (
                <>
                    <div className="drawer-overlay" onClick={closeDrawer} />
                    <div className={`profile-drawer ${drawerOpen ? 'open' : ''}`}>
                        <div className="drawer-header">
                            <h2>{selectedCompany?.CompanyName}</h2>
                            <button className="drawer-close-btn" onClick={closeDrawer} aria-label="Close drawer">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="drawer-content">
                            {/* Entity Tag */}
                            <span 
                                className="drawer-entity-tag"
                                style={tableData?.event_entity_color ? {
                                    '--entity-bg-color': `${tableData.event_entity_color}20`,
                                    '--entity-border-color': `${tableData.event_entity_color}40`,
                                    '--entity-text-color': tableData.event_entity_color
                                } : undefined}
                            >{tableData?.event_entity_name}</span>

                            {/* Organization Contacts */}
                            {selectedCompany?.contacts && selectedCompany.contacts.length > 0 && (
                                <div className="drawer-contacts-section">
                                    {selectedCompany.contacts.map((contact) => (
                                        <div 
                                            key={contact.ContactId} 
                                            className="drawer-contact-item"
                                            onClick={() => window.open(`/user/${contact.ContactId}`, '_blank')}
                                            title="View profile"
                                        >
                                            {contact.ImageUrl && (
                                                <img
                                                    src={contact.ImageUrl}
                                                    alt={`${contact.FirstName} ${contact.LastName}`}
                                                    className="drawer-contact-avatar"
                                                />
                                            )}
                                            {!contact.ImageUrl && (
                                                <div className="drawer-contact-avatar-placeholder">
                                                    {contact.FirstName?.[0]}{contact.LastName?.[0]}
                                                </div>
                                            )}
                                            <div className="drawer-contact-info">
                                                <div className="drawer-contact-name">
                                                    {contact.FirstName} {contact.LastName}
                                                </div>
                                                {contact.JobTitle && (
                                                    <div className="drawer-contact-title">
                                                        {contact.JobTitle}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {tableData?.questions
                                .filter((question) => {
                                    const value = selectedCompany?.answers[question.question_id];
                                    if (value === null || value === undefined || value === '') {
                                        return false;
                                    }
                                    // Hide specific financial questions
                                    const questionText = (question.short_description || question.question_text || '').toLowerCase();
                                    if (questionText.includes('average annual philanthropic giving') && questionText.includes('last three years')) {
                                        return false;
                                    }
                                    if (questionText.includes('philanthropic giving projected to grow')) {
                                        return false;
                                    }
                                    if (questionText.includes('projected capacity to give to new charitable organizations')) {
                                        return false;
                                    }
                                    return true;
                                })
                                .map((question) => {
                                    const value = selectedCompany?.answers[question.question_id];
                                    
                                    // Transform certain labels for better display
                                    let labelText = question.short_description || question.question_text;
                                    if (labelText?.toLowerCase().includes('primary areas of philanthropic giving')) {
                                        labelText = 'Areas of Philanthropic Giving';
                                    }
                                    if (labelText?.toLowerCase().includes('geographic focus') && labelText?.toLowerCase().includes('select as many')) {
                                        labelText = 'Geographic Focus';
                                    }
                                    if (labelText?.toLowerCase().includes('potential geographies for future giving')) {
                                        labelText = 'Potential Regions For Future Giving';
                                    }
                                    if (labelText?.toLowerCase().includes('current approach to philanthropic giving')) {
                                        labelText = 'Current Approach To Philanthropic Giving';
                                    }
                                    // Financials - truncate long description
                                    if (labelText?.toLowerCase().startsWith('financials')) {
                                        labelText = 'Financials';
                                    }
                                    // Affinity group question
                                    if (labelText?.toLowerCase().includes('affinity group programming')) {
                                        labelText = 'Affinity Group Interests';
                                    }
                                    // Question 53 - Giving Regions
                                    if (question.question_id === 53) {
                                        labelText = 'Giving Regions';
                                    }
                                    // Question 85 - Geographic Focus
                                    if (question.question_id === 85) {
                                        labelText = 'Geographic Focus';
                                    }
                                    // Generic removal of "(Check All That Apply)" and similar phrases
                                    if (labelText) {
                                        labelText = labelText.replace(/\s*\(check all that apply\)/gi, '');
                                        labelText = labelText.replace(/\s*\(select as many as apply\)/gi, '');
                                    }

                                    return (
                                        <div key={question.question_id} className="drawer-field">
                                            <label className="drawer-field-label">
                                                {labelText}
                                            </label>
                                            <div className="drawer-field-value">
                                                {renderDrawerValue(value, question)}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                        <div className="drawer-footer">
                            {(() => {
                                const icEventCompanyId = selectedCompany?.ic_event_company_id 
                                    ? parseInt(selectedCompany.ic_event_company_id, 10) 
                                    : null;
                                const meetingInfo = getMeetingStatusForCompany(icEventCompanyId);
                                
                                if (meetingInfo) {
                                    const { status, meeting } = meetingInfo;
                                    
                                    if (status === 'confirmed') {
                                        return (
                                            <button 
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <CheckCircle size={14} />
                                                Meeting Confirmed
                                            </button>
                                        );
                                    }
                                    
                                    if (status === 'pending_sent') {
                                        return (
                                            <button 
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <Clock size={14} />
                                                Meeting Pending
                                            </button>
                                        );
                                    }
                                    
                                    if (status === 'pending_received') {
                                        return (
                                            <button 
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <Clock size={14} />
                                                Meeting Action Required
                                            </button>
                                        );
                                    }

                                    if (status === 'cancelled') {
                                        return (
                                            <button 
                                                className="drawer-meet-btn meeting-status-btn"
                                                onClick={() => {
                                                    setSelectedMeetingId(meeting.meetingId);
                                                    setSelectedMeetingCompanyName(selectedCompany.CompanyName);
                                                    setMeetingDrawerOpen(true);
                                                }}
                                            >
                                                <X size={14} />
                                                Meeting Cancelled
                                            </button>
                                        );
                                    }
                                }
                                
                                // Default: No meeting - show Meet button
                                return (
                                    <button 
                                        className="drawer-meet-btn"
                                        onClick={() => handleMeetRequest(selectedCompany)}
                                    >
                                        Meet
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </>
            )}

            {/* Meeting Details Drawer */}
            <MeetingDetailsDrawer
                meetingId={selectedMeetingId}
                isOpen={meetingDrawerOpen}
                onClose={handleMeetingDrawerClose}
                onMeetingCancelled={handleMeetingAction}
                counterpartyCompanyName={selectedMeetingCompanyName}
            />

            {/* Meeting Request Drawer */}
            <MeetingRequestDrawer
                isOpen={meetingRequestDrawerOpen}
                onClose={handleMeetingRequestClose}
                contact={meetingRequestContact}
                eventId={event?.EventId}
                icEventId={event?.ic_event_id}
                onSuccess={handleMeetingRequestSuccess}
            />
        </>
    );
};

export default EventPhilanthropists;
